import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getMajorClubs } from "../../../api/ClubApi";
import { getAllEvents } from "../../../api/EventApi";
import { updatePersonnelStatus } from "../../../api/PersonnelApi";
import { getActiveUmpires } from "../../../api/UmpireGuestsApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useAffiliation } from "../../../contexts/AffiliationContext";
import { useAlert } from "../../../contexts/AlertsContext";
import { BigsClub } from "../../../types/personnel";
import { BigsSelect } from "../BigsBcrWrapper";
import { RequiredLabel } from "../FormElements";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

type ReactivatePersonnelBodyProps = {
    personnel: any;
    callback: Function;
    selectedOrg: any;
    setSelectedOrg: Function;
    selectedAffiliation: any;
    setSelectedAffiliation: Function;
};

const BodyContent = styled.div.attrs(() => ({
    className: "mt-4 ml-4",
}))`
    text-align: left;
    margin-bottom: 200px;
`;

const TopTextStyle = styled.div.attrs(() => ({}))`
    &&& {
        font-size: 16px;
        color: var(--slate);
        line-height: 24px;
        text-style: Helvetica;
    }
`;

const BottomTextStyle = styled.div.attrs(() => ({}))`
    &&& {
        font-size: 12px;
        text-style: Helvetica;
        font-weight: 300;
        width: 400px;
    }
`;

const mapEventsToOptions = (response: any[]) => {
    const safeList = (Array.isArray(response) ? response : []).filter(
        (event: any) => event?.id != null && event?.name,
    );
    return safeList
        .map((event: any) => ({
            value: event.id,
            label: event.name,
            id: event.id,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
};

const ReactivatePersonnelBody: React.FC<ReactivatePersonnelBodyProps> = ({
    personnel,
    selectedOrg,
    setSelectedOrg,
    setSelectedAffiliation,
    selectedAffiliation,
}) => {
    const { affiliationDropdownOptions, refreshClubDropdownByOrgStatsId } = useAffiliation();
    const [orgDropdownOptions, setOrgDropdownOptions] = useState<any[]>([]);

    const personnelType = personnel?.personnelType?.type;
    const isClubPersonnel = personnelType === PersonnelTypes.CLUB;
    const isUmpirePersonnel = personnelType === PersonnelTypes.UMPIRE;
    const isVolunteerPersonnel = !isClubPersonnel && !isUmpirePersonnel;

    useEffect(() => {
        if (isClubPersonnel) {
            getMajorClubs().then((response: BigsClub[]) => {
                const safeList = (Array.isArray(response) ? response : []).filter(
                    (club: BigsClub) => club?.orgStatsId != null && club?.name,
                );
                const orgOptions = safeList
                    .map((club: BigsClub) => ({
                        value: club.clubStatsId,
                        label: club.name,
                        teamId: club.clubStatsId,
                        orgId: club.orgStatsId,
                        clubStatsId: club.clubStatsId,
                    }))
                    .sort((a: any, b: any) => a.label.localeCompare(b.label));
                setOrgDropdownOptions(orgOptions);
            });
            return;
        }

        if (isUmpirePersonnel) {
            getActiveUmpires().then(response => {
                const safeList = (Array.isArray(response) ? response : []).filter(
                    (umpire: any) => umpire?.umpireStatsId != null && umpire?.name,
                );
                setOrgDropdownOptions(
                    safeList
                        .map((umpire: any) => ({
                            value: umpire.umpireStatsId,
                            label: umpire.name,
                            teamId: umpire.umpireStatsId,
                            orgId: umpire.umpireStatsId,
                        }))
                        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
                );
            });
            return;
        }

        // VMS / volunteer: events only
        getAllEvents().then(response => {
            setOrgDropdownOptions(mapEventsToOptions(response));
        });
    }, [personnelType, isClubPersonnel, isUmpirePersonnel]);

    const orgChangeHandler = (orgOption: any) => {
        setSelectedOrg(orgOption);
        setSelectedAffiliation(null); // Clear team selection when org changes
        if (isClubPersonnel) {
            refreshClubDropdownByOrgStatsId(orgOption.orgId);
        } else if (isUmpirePersonnel) {
            setSelectedAffiliation(orgOption);
        }
        // VMS: event lives on selectedOrg only (value/id = bigs.event.id); do not set selectedAffiliation
    };

    const affiliationChangeHandler = (affiliationOption: any) => {
        setSelectedAffiliation(affiliationOption);
    };

    const primaryPickerLabel = isVolunteerPersonnel ? "Event" : "Organization";

    return (
        <BodyContent>
            <TopTextStyle>
                Add {personnel.lastName}, {personnel.firstName} to the following Roster.
            </TopTextStyle>
            <div className="mr-1 mt-2">
                <RequiredLabel text={primaryPickerLabel} required />
                <BottomTextStyle>
                    <BigsSelect
                        value={selectedOrg}
                        isDisabled={false}
                        onChange={(orgOpt: any) => {
                            orgChangeHandler(orgOpt);
                        }}
                        options={orgDropdownOptions}
                        placeholder="select..."
                    />
                </BottomTextStyle>
            </div>
            {!isUmpirePersonnel && !isVolunteerPersonnel ? (
                <div className="mr-1 mt-2">
                    <RequiredLabel text="Team" required />
                    <BottomTextStyle>
                        <BigsSelect
                            value={selectedAffiliation}
                            isDisabled={!selectedOrg}
                            onChange={(affiliationOpt: any) => {
                                affiliationChangeHandler(affiliationOpt);
                            }}
                            options={affiliationDropdownOptions}
                            placeholder="select..."
                        />
                    </BottomTextStyle>
                </div>
            ) : null}
        </BodyContent>
    );
};

type ReactivatePersonnelFooterProps = {
    personnel: {
        id: number;
        status: string;
        affiliation: any;
        personnelType?: { type?: string };
    };
    closeModal: Function;
    callback: Function;
    selectedOrg: any;
    selectedAffiliation: any;
};

const ReactivatePersonnelFooter: React.FC<ReactivatePersonnelFooterProps> = ({
    personnel,
    closeModal,
    callback,
    selectedOrg,
    selectedAffiliation,
}) => {
    const showAlert = useAlert();
    const isUmpirePersonnel = personnel?.personnelType?.type === PersonnelTypes.UMPIRE;
    const isClubPersonnel = personnel?.personnelType?.type === PersonnelTypes.CLUB;
    const isVolunteerPersonnel = !isClubPersonnel && !isUmpirePersonnel;
    const addPersonnelHandler = () => {
        // CLUB: clubStatsId; UMPIRE: umpireStatsId; VMS: event id from selectedOrg only (not affiliation).
        const assignmentId = isVolunteerPersonnel
            ? selectedOrg?.value ?? selectedOrg?.id
            : selectedAffiliation?.clubStatsId ?? selectedAffiliation?.teamId ?? selectedOrg?.value;
        updatePersonnelStatus(personnel.id, "ACTIVE", assignmentId)
            .then(() => {
                if (callback && typeof callback === "function") {
                    callback(isVolunteerPersonnel ? selectedOrg : selectedAffiliation);
                }
                showAlert("Added to Roster");
            })
            .catch(() => {
                showAlert("Error adding personnel to roster", "danger");
            });
    };

    const isAddDisabled = isVolunteerPersonnel
        ? !selectedOrg
        : !selectedOrg || (!isUmpirePersonnel && !selectedAffiliation);

    return (
        <div className="d-flex">
            <PrimaryButtonStyled
                height={26}
                width={180}
                disabled={isAddDisabled}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                    addPersonnelHandler();
                }}
            >
                Add to Roster
            </PrimaryButtonStyled>
            <div className="pl-2">
                <OutlinedButtonStyled
                    width={80}
                    height={26}
                    disabled={false}
                    onClick={event => {
                        event.stopPropagation();
                        closeModal();
                    }}
                >
                    Cancel
                </OutlinedButtonStyled>
            </div>
        </div>
    );
};

ReactivatePersonnelBody.displayName = "BodyContent";
ReactivatePersonnelFooter.displayName = "FooterContent";

export { ReactivatePersonnelBody, ReactivatePersonnelFooter };
